import { useTranslation } from 'react-i18next';
import LinksButton from 'shared/design-system/components/links-button';
import YearSelect from 'shared/_components/year-select';
import { formatCurrency, getFormattedDateHeader, getFormattedDateString } from 'shared/utils/common.utils';
import useGetWbsTableData from '../../_hooks/wbs/useGetWbsTableData';
import NoData from 'shared/design-system/components/no-data-box';
import { Table } from 'react-bootstrap';
import { Dropdown, Input, InputNumber, MenuProps, Tooltip } from 'antd';
import { StaffExtendedDto, WbsListDto, WbsPlanningDto } from '@api/client';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import React, { useEffect, useRef } from 'react';
import useWbsManagement from '../../_hooks/wbs/useWbsManagement';
import useValidationWbs from '../../_hooks/wbs/useValidationWbs';
import useGetWbsTotals from '../../_hooks/wbs/useGetWbsTotals';
import useSynchronizedScroll from '../../_hooks/useSynchronizedScroll';
import { TableProjectKeys } from 'enums/project.enum';
import WbsTotalsTable from '../wbs-totals-table';

interface Props {
  idProject: number;
  readOnly: boolean | undefined;
  years: (number | undefined)[];
  isWorkInProgress: boolean;
  fullAllocations: Partial<StaffExtendedDto>[] | null | undefined;
  totalRevenues: number;
  setYears: React.Dispatch<React.SetStateAction<(number | undefined)[]>>;
  setTotalDeliverablesValidation: React.Dispatch<React.SetStateAction<boolean>>;
  isWbsTotalsCollapsed: boolean;
  setIsWbsTotalsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

const Wbs: React.FC<Props> = ({
  idProject,
  readOnly,
  years,
  isWorkInProgress,
  fullAllocations,
  totalRevenues,
  setYears,
  setTotalDeliverablesValidation,
  isWbsTotalsCollapsed,
  setIsWbsTotalsCollapsed,
}) => {
  const { t } = useTranslation();

  const tableRef = useRef<HTMLDivElement>(null);

  const {
    deliverablesTableData,
    deliverablesTableDataShown,
    // years,
    currentYear,
    totalForecastDeliverables,
    filterAllocationsByYear,
    setDeliverablesTableData,
    setTotalForecastDeliverables,
  } = useGetWbsTableData(idProject);

  const {
    handleChangeWbsField,
    handleChangeAllocation,
    handleChangeTotalForecastDeliverables,
    addDeliverable,
    deleteDeliverable,
    clearForecastAmounts,
  } = useWbsManagement(
    deliverablesTableData,
    setDeliverablesTableData,
    filterAllocationsByYear,
    currentYear,
    setTotalForecastDeliverables,
    fullAllocations,
    idProject
  );

  const { areTotalsDeliverablesMatch, getTooltip } = useValidationWbs({
    totalForecastDeliverables,
    deliverablesTableData,
    setTotalDeliverablesValidation,
  });

  const { totalPercentagePerMonth, totalCostPerMonth } = useGetWbsTotals(deliverablesTableDataShown, totalRevenues);

  // Riusa lo stesso hook di sincronizzazione scroll usato dal tab Team (Team ne mantiene la propria istanza
  // in revenue-plan/index.tsx). Qui viene istanziato una seconda volta per sincronizzare la tabella mesi
  // principale della WBS con WbsTotalsTable, senza toccare il file dell'hook (nessun rischio di regressione
  // sul tab Team).
  // - handleParentScroll legge solo `event.target.scrollLeft` per scrivere su `totalsTableRef`, quindi si
  //   collega direttamente all'evento onScroll del container principale WBS (tableRef) senza bisogno di
  //   unificare i ref: nessun cast necessario.
  // - handleChildScroll dell'hook scrive invece su `teamTableRef`/`otherCostsTableRef` (tipizzati
  //   HTMLTableElement, non applicabili al container WBS che è un HTMLDivElement), quindi per la direzione
  //   totali → principale viene usato un handler locale type-safe (vedi handleWbsChildScroll sotto).
  const {
    totalsTableRef: wbsMonthsTotalsRef,
    handleParentScroll: handleWbsParentScroll,
  } = useSynchronizedScroll(undefined, years, TableProjectKeys.WBS);

  const handleWbsChildScroll = (scrollLeft: number) => {
    if (tableRef.current) {
      tableRef.current.scrollLeft = scrollLeft;
    }
  };

  const toggleWbsTotalsCollapsed = () => setIsWbsTotalsCollapsed((prev) => !prev);

  const getFirstAllocationDate = () => {
    if (!deliverablesTableDataShown || deliverablesTableDataShown.length === 0) return null;

    const firstAllocation = deliverablesTableDataShown[0]?.wbsPlanningDtoList?.[0];
    if (!firstAllocation) return null;

    return getFormattedDateString(firstAllocation.mese, firstAllocation.anno);
  };

  const getLastAllocationDate = () => {
    if (!deliverablesTableDataShown || deliverablesTableDataShown.length === 0) return null;

    const lastAllocation =
      deliverablesTableDataShown[deliverablesTableDataShown.length - 1]?.wbsPlanningDtoList?.slice(-1)[0];
    if (!lastAllocation) return null;

    return getFormattedDateString(lastAllocation.mese, lastAllocation.anno);
  };

  const handleMenuClick: (rowIndex: number) => MenuProps['onClick'] = (rowIndex) => (e) => {
    switch (e.key) {
      case '1':
        clearForecastAmounts(rowIndex);
        break;
      case '2':
        deleteDeliverable(rowIndex, deliverablesTableData?.[rowIndex]?.id);
        break;
    }
  };

  const generateMenuItems = (rowIndex: number) => {
    return [
      {
        label: 'Clear Forecast Progress',
        key: '1',
        icon: (
          <i
            className={`icon-pulisci text-xl ${
              Number(totalForecastDeliverables?.[rowIndex]) <= 0 ? 'lightgrey' : 'text-blue-links'
            }`}
          />
        ),
        danger: false,
        disabled: Number(totalForecastDeliverables?.[rowIndex]) <= 0,
      },
      {
        label: 'Delete Deliverable',
        key: '2',
        icon: <i className="icon-delete text-xl" />,
        danger: true,
        disabled: !isDeleteDeliverableEnabled(rowIndex),
      },
    ];
  };

  const isDeleteDeliverableEnabled = (index: number) => {
    const actualAmounts =
      Number(
        deliverablesTableData?.[index].wbsPlanningDtoList
          ?.filter((el) => el.enable === false)
          ?.reduce((acc, el) => acc + (el.corrispettivo ?? 0), 0)
      ) ?? 0;
    return actualAmounts === 0;
  };

  const menuProps = (rowIndex: number) => ({
    items: generateMenuItems(rowIndex),
    onClick: handleMenuClick(rowIndex),
  });

  const getActualAmounts = (index: number) => {
    return deliverablesTableData?.[index].wbsPlanningDtoList?.filter((el) => el.enable === false);
  };

  const getTotSalPercentage = (index: number) =>
    (Number(totalForecastDeliverables?.[index]) || 0) +
    (Number(getActualAmounts(index)?.reduce((acc, el) => acc + (el.corrispettivo ?? 0), 0)) || 0);

  const getTotSalCost = (index: number, deliverable: WbsListDto) =>
    ((totalRevenues / 100) * (deliverable.pesoAttivita ?? 0) * (Number(totalForecastDeliverables?.[index]) || 0)) /
      100 +
    (Number(
      getActualAmounts(index)?.reduce(
        (acc, el) => acc + ((totalRevenues / 100) * (deliverable.pesoAttivita ?? 0) * (el.corrispettivo ?? 0)) / 100,
        0
      )
    ) || 0);

  const totalTotSalPercentage =
    deliverablesTableDataShown?.reduce((acc, _deliverable, index) => acc + getTotSalPercentage(index), 0) ?? 0;

  const totalTotSalCost =
    deliverablesTableDataShown?.reduce((acc, deliverable, index) => acc + getTotSalCost(index, deliverable), 0) ?? 0;

  useEffect(() => {
    const monthsWorked = deliverablesTableData?.[0]?.wbsPlanningDtoList?.filter((el) => !el.enable).length ?? 0;
    if (monthsWorked) scrollToRight(monthsWorked * 100);
  }, []);

  const scrollToRight = (px: number) => {
    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.scrollBy({
          top: 0,
          left: px,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  return (
    <>
      <div className="d-flex justify-content-between mb-3" id="header-buttons">
        <div className="col-6">
          <LinksButton className="btn-sm btn btn-links-outline px-3" isDisabled={readOnly} onClick={addDeliverable}>
            <div className="d-flex align-items-center">
              <i className="fas fa-plus pe-1" />
              {t('Add deliverable')}
            </div>
          </LinksButton>
        </div>
        <span className="ms-auto me-5 my-auto">
          <span className="mx-1 notification-header"> {t('project-detail.period')} :</span>
          <span className="mx-1">
            <b>{getFirstAllocationDate()}</b>
          </span>
          -
          <span className="mx-1">
            <b>{getLastAllocationDate()}</b>
          </span>
        </span>
        <YearSelect years={years} defaultValue={'all'} onChange={(e) => filterAllocationsByYear(e)} />
      </div>
      {deliverablesTableData?.length === 0 ? (
        <NoData msg={t('No deliverables found')} />
      ) : (
        <>
        <div
          ref={tableRef}
          className="d-flex table-responsive table-bordered container-table"
          style={{ maxHeight: 'calc(100vh - 420px)', overflowY: 'auto', overflowX: 'auto' }}
          onScroll={handleWbsParentScroll}
        >
          <Table className={'revenue-table sticky-table mt-0 mb-0'}>
            <thead>
              <tr key="header-deliverables">
                <th></th>
                <th className="text-left">
                  <span>{t('Name')}</span>
                </th>
                <th className="text-left">
                  <span>{t('Weight (%)')}</span>
                  <Tooltip
                    title={t('Percentage weight of the deliverable in relation to the project’s total revenue')}
                    color="white"
                  >
                    <i className="fas fa-circle-info text-blue-links ps-2" />
                  </Tooltip>
                </th>
                <th className="text-left" style={{ minWidth: '150px', maxWidth: '150px' }}>
                  <span>{t('Remaining')}</span>
                </th>
                {isWorkInProgress && (
                  <>
                    <th className="text-left bg-disabled" style={{ minWidth: '150px', maxWidth: '150px' }}>
                      <span>{t('Completed')}</span>
                    </th>
                    <th className="text-left bg-disabled" style={{ minWidth: '150px', maxWidth: '150px' }}>
                      <span>{t('Tot SAL')}</span>
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {deliverablesTableDataShown?.map((deliverable: WbsListDto, index: number) => (
                <>
                  <tr key={'wbs_row_percentage_' + index}>
                    <td className="text-center p-0" style={{ minWidth: '20px', maxWidth: '20px' }}>
                      %
                    </td>
                    <td className="text-left p-0" style={{ minWidth: '270px', maxWidth: '270px' }}>
                      <Input
                        value={deliverable?.titolo}
                        placeholder={t('Insert a name')}
                        disabled={readOnly}
                        onChange={(e) => handleChangeWbsField(e.target.value, index, 'titolo')}
                        className="h-100"
                      />
                    </td>
                    <td className="text-left p-0" style={{ minWidth: '150px', maxWidth: '150px' }}>
                      <InputNumber
                        min={0}
                        max={100}
                        step="0.01"
                        decimalSeparator=","
                        value={deliverable?.pesoAttivita}
                        placeholder={t('Insert the activity weight')}
                        disabled={readOnly}
                        onChange={(e) => handleChangeWbsField(e, index, 'pesoAttivita')}
                        className="h-100 w-100 border-0"
                      />
                    </td>
                    <td className="text-left p-0" style={{ minWidth: '150px', maxWidth: '150px' }}>
                      <div className="d-flex justify-content-between h-100">
                        <Tooltip
                          title={!areTotalsDeliverablesMatch?.[index] ? getTooltip(index) : ''}
                          color="red"
                          key={`tooltip-total-percentage-${index}`}
                          className="w-100"
                        >
                          <Input
                            value={formatCurrency(Number(totalForecastDeliverables[index]) ?? 0)}
                            disabled={readOnly}
                            readOnly
                            variant="borderless"
                            // className={!areTotalsDeliverablesMatch?.[index] ? 'red-label' : ''}
                            // suffix={
                            //   <Tooltip title={`text`} key={`suffix-tooltip-percentage-${index}`}>
                            //     <ExclamationCircleOutlined
                            //       className="pe-3"
                            //       style={{
                            //         color: !areTotalsDeliverablesMatch?.[index] ? 'red' : 'transparent',
                            //       }}
                            //     />
                            //   </Tooltip>
                            // }
                          />
                        </Tooltip>
                        <Tooltip title={'Actions'} color="white" key={`actions-${index}`}>
                          <Dropdown menu={menuProps(index)} trigger={['click']}>
                            <i
                              className="fa-solid fa-ellipsis-vertical text-blue-links pt-2 pe-3 ps-1"
                              role="button"
                              onClick={(e) => e.preventDefault()}
                            />
                          </Dropdown>
                        </Tooltip>
                      </div>
                    </td>

                    {isWorkInProgress && (
                      <>
                        <td
                          className="text-left p-0 bg-disabled"
                          key={'Utilizzati-percentage-' + index}
                          style={{ minWidth: '150px', maxWidth: '150px' }}
                        >
                          <Input
                            readOnly
                            className="bg-disabled"
                            value={formatCurrency(
                              Number(getActualAmounts(index)?.reduce((acc, el) => acc + (el.corrispettivo ?? 0), 0)) ??
                                0
                            )}
                          />
                        </td>
                        <td
                          className="text-left p-0 bg-disabled"
                          key={'Tot/Pian-percentage-' + index}
                          style={{ minWidth: '150px', maxWidth: '150px' }}
                        >
                          <Input
                            readOnly
                            className="bg-disabled"
                            value={formatCurrency(
                              totalForecastDeliverables?.[index] +
                                Number(getActualAmounts(index)?.reduce((acc, el) => acc + (el.corrispettivo ?? 0), 0))
                            )}
                          />
                        </td>
                      </>
                    )}
                  </tr>

                  <tr key={'wbs_row_currency_' + index}>
                    <td className="text-left py-0" colSpan={3}>
                      {`Deliverable value (€) ${deliverable.titolo ? `- ${deliverable.titolo}` : ''}`}
                    </td>
                    <td className="text-left p-0" style={{ minWidth: '150px', maxWidth: '150px' }}>
                      <div className="d-flex justify-content-between h-100">
                        <Tooltip
                          title={!areTotalsDeliverablesMatch?.[index] ? getTooltip(index) : ''}
                          color="red"
                          key={`tooltip-total-currency-${index}`}
                          className="w-100"
                        >
                          <Input
                            value={formatCurrency(
                              ((totalRevenues / 100) *
                                deliverable.pesoAttivita! *
                                (Number(totalForecastDeliverables[index]) ?? 0)) /
                                100
                            )}
                            disabled={readOnly}
                            readOnly
                            variant="borderless"
                            // className={!areTotalsDeliverablesMatch?.[index] ? 'red-label' : ''}
                            // suffix={
                            //   <Tooltip title={`text`} key={`suffix-tooltip-currency-${index}`}>
                            //     <ExclamationCircleOutlined
                            //       className="pe-3"
                            //       style={{
                            //         color: !areTotalsDeliverablesMatch?.[index] ? 'red' : 'transparent',
                            //       }}
                            //     />
                            //   </Tooltip>
                            // }
                          />
                        </Tooltip>
                      </div>
                    </td>

                    {isWorkInProgress && (
                      <>
                        <td
                          className="text-left p-0 bg-disabled"
                          key={'Utilizzati-currency-' + index}
                          style={{ minWidth: '150px', maxWidth: '150px' }}
                        >
                          <Input
                            readOnly
                            className="bg-disabled"
                            value={formatCurrency(
                              Number(
                                getActualAmounts(index)?.reduce(
                                  (acc, el) =>
                                    acc +
                                    ((totalRevenues / 100) * deliverable.pesoAttivita! * (el.corrispettivo ?? 0)) / 100,
                                  0
                                )
                              ) ?? 0
                            )}
                            // ((totalRevenues / 100) * deliverable.pesoAttivita! * (allocation.corrispettivo ?? 0)) / 100
                          />
                        </td>
                        <td
                          className="text-left p-0 bg-disabled"
                          key={'Tot/Pian-currency-' + index}
                          style={{ minWidth: '150px', maxWidth: '150px' }}
                        >
                          <Input
                            readOnly
                            className="bg-disabled"
                            value={formatCurrency(
                              ((totalRevenues / 100) *
                                deliverable.pesoAttivita! *
                                (Number(totalForecastDeliverables[index]) ?? 0)) /
                                100 +
                                Number(
                                  getActualAmounts(index)?.reduce(
                                    (acc, el) =>
                                      acc +
                                      ((totalRevenues / 100) * deliverable.pesoAttivita! * (el.corrispettivo ?? 0)) /
                                        100,
                                    0
                                  )
                                )
                            )}
                          />
                        </td>
                      </>
                    )}
                  </tr>
                </>
              ))}
            </tbody>
          </Table>
          <Table className={'revenue-table mt-0 mb-0'}>
            <thead>
              <tr>
                {deliverablesTableDataShown?.[0]?.wbsPlanningDtoList?.map(
                  (allocation: WbsPlanningDto, index: number) => (
                    <th
                      className={`text-left ${allocation?.enable === false && 'bg-disabled'}`}
                      scope="col"
                      key={'wbs-alloc-month-percentage' + index}
                      style={{ minWidth: '100px', maxWidth: '100px' }}
                    >
                      <span>{getFormattedDateHeader(allocation?.mese, allocation?.anno)}</span>
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {deliverablesTableDataShown?.map((deliverable: WbsListDto, index: number) => (
                <>
                  <tr key={'wbs-alloc-row-percentage-' + index}>
                    {deliverable?.wbsPlanningDtoList?.map((allocation: WbsPlanningDto, index: number) => (
                      <td
                        className={`text-center p-0 ${allocation?.enable === false && 'bg-disabled'}`}
                        key={'wbs-alloc-percentage-' + index}
                        style={{ minWidth: '100px', maxWidth: '100px' }}
                      >
                        <InputNumber
                          className="w-100"
                          key={'wbs-alloc-input-percentage-' + index}
                          value={allocation.corrispettivo}
                          decimalSeparator=","
                          readOnly={readOnly}
                          disabled={allocation?.enable === false || !deliverable.pesoAttivita}
                          min={0}
                          onChange={(e) => handleChangeAllocation(e, allocation?.id)}
                          variant="borderless"
                        />
                      </td>
                    ))}
                  </tr>
                  <tr key={'wbs-alloc-row-currency-' + index}>
                    {deliverable?.wbsPlanningDtoList?.map((allocation: WbsPlanningDto, index: number) => (
                      <td
                        className={`text-center p-0 ${allocation?.enable === false && 'bg-disabled'}`}
                        key={'wbs-alloc-currency' + index}
                        style={{ minWidth: '100px', maxWidth: '100px' }}
                      >
                        <Input
                          className="w-100"
                          key={'wbs-alloc-input-currency-' + index}
                          value={formatCurrency(
                            ((totalRevenues / 100) * deliverable.pesoAttivita! * (allocation.corrispettivo ?? 0)) / 100
                          )}
                          readOnly={readOnly}
                          disabled={true}
                          min={0}
                          variant="borderless"
                        />
                      </td>
                    ))}
                  </tr>
                </>
              ))}
            </tbody>
          </Table>
        </div>
        <WbsTotalsTable
          deliverablesTableDataShown={deliverablesTableDataShown}
          totalPercentagePerMonth={totalPercentagePerMonth}
          totalCostPerMonth={totalCostPerMonth}
          totalTotSalPercentage={totalTotSalPercentage}
          totalTotSalCost={totalTotSalCost}
          isWorkInProgress={isWorkInProgress}
          isCollapsed={isWbsTotalsCollapsed}
          toggleCollapse={toggleWbsTotalsCollapsed}
          totalsScrollRef={wbsMonthsTotalsRef}
          onChildScroll={handleWbsChildScroll}
        />
        </>
      )}
    </>
  );
};

export default Wbs;
